import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

// Piecewise supervisor dashboard hooks. Each one drives a single section
// so the page can paint as soon as its slice resolves instead of gating
// everything on a single multi-query waterfall (the per-AM revenue
// breakdown rollup + top-physician $lookup chain were the bottlenecks).
// The combined endpoint is still available via
// apiClient.getSupervisorAnalytics() — Analytics.tsx and other callers
// are unaffected.

type DateRange = { startDate?: Date; endDate?: Date } | undefined;
type Intervals = { revenueInterval?: string } | undefined;

// Date ranges are Date objects so we can't use them as query keys
// directly — serialize to ISO strings (or "undefined") for stable,
// comparable keys.
const dateRangeKey = (dateRange: DateRange) => ({
  start: dateRange?.startDate?.toISOString() ?? null,
  end: dateRange?.endDate?.toISOString() ?? null,
});

export function useSupervisorHeader(dateRange: DateRange) {
  return useQuery({
    queryKey: ['supervisor-analytics', 'header', dateRangeKey(dateRange)],
    queryFn: async () => {
      const response = await apiClient.getSupervisorAnalyticsHeader(dateRange);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load header analytics');
      }
      return response.data;
    },
  });
}

export function useSupervisorTrends(dateRange: DateRange, intervals: Intervals) {
  return useQuery({
    queryKey: [
      'supervisor-analytics',
      'trends',
      dateRangeKey(dateRange),
      intervals?.revenueInterval ?? 'monthly',
    ],
    queryFn: async () => {
      const response = await apiClient.getSupervisorAnalyticsTrends(dateRange, intervals);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load trends analytics');
      }
      return response.data;
    },
  });
}

export function useSupervisorRankings(dateRange: DateRange) {
  return useQuery({
    queryKey: ['supervisor-analytics', 'rankings', dateRangeKey(dateRange)],
    queryFn: async () => {
      const response = await apiClient.getSupervisorAnalyticsRankings(dateRange);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load rankings analytics');
      }
      return response.data;
    },
    // Rankings is the slow slice (getTopPhysicians does an Order/Invoice
    // $lookup chain across the whole network). Cache it longer than the
    // default and don't refetch on remount so navigating back to the
    // dashboard is instant.
    staleTime: 5 * 60_000,
    refetchOnMount: false,
  });
}
